/**
 * SessionTimeoutCountdown
 *
 * Counts down from initialSeconds to 0, ticking once per second.
 * Formats the remaining time as m:ss and calls onExpired when it reaches 0.
 *
 * SCRUM-366 | MVP 1.15 V2 Epic 4
 */

import React, { useState, useEffect } from 'react';

/**
 * @param {number} totalSeconds - non-negative integer
 * @returns {string} e.g. "4:59", "1:00", "0:30"
 */
export function formatCountdown(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(s / 60);
  const seconds = s % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * @param {{
 *   initialSeconds: number,
 *   onExpired: () => void,
 *   onTick?: (remaining: number) => void,
 * }} props
 */
export default function SessionTimeoutCountdown({ initialSeconds, onExpired, onTick }) {
  const [remaining, setRemaining] = useState(Math.max(0, Math.floor(initialSeconds)));

  useEffect(() => {
    if (remaining <= 0) {
      onExpired();
      return;
    }

    const id = setInterval(() => {
      setRemaining(prev => {
        const next = prev - 1;
        if (onTick) { onTick(next); }
        return next;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [remaining]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <span data-testid="session-countdown">
      {formatCountdown(remaining)}
    </span>
  );
}
